import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';

function LightMeter({
  value,
  min,
  max,
  size = 140,
  optimalMin,
  optimalMax,
  color,
}) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);

  // Scale factor for fonts (based on default size of 140)
  const scale = size / 140;

  const centerX = size / 2;
  const centerY = size / 2 - 10;
  const sunRadius = size * 0.12;
  const rayCount = 12;
  const maxRayLength = size * 0.22;

  // Determine color based on value, or use custom color
  const getColor = () => {
    if (color) return color;
    if (value < optimalMin) return '#eab308';
    if (value > optimalMax) return '#ef4444';
    return '#fbbf24';
  };

  const lightColor = getColor();
  const intensity = normalizedValue;

  // Generate ray paths with consistent pattern (no random)
  const rays = Array.from({ length: rayCount }).map((_, index) => {
    const angle = (index / rayCount) * 2 * Math.PI - Math.PI / 2;
    // Use sine wave for consistent variation pattern
    const variation = 0.7 + Math.sin(index * 1.5) * 0.15 + 0.15;
    const rayLength = maxRayLength * intensity * variation;
    const innerRadius = sunRadius + 6;
    const outerRadius = sunRadius + 6 + rayLength;

    const x1 = centerX + Math.cos(angle) * innerRadius;
    const y1 = centerY + Math.sin(angle) * innerRadius;
    const x2 = centerX + Math.cos(angle) * outerRadius;
    const y2 = centerY + Math.sin(angle) * outerRadius;

    return { x1, y1, x2, y2, angle, rayLength };
  });

  return (
    <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: size,
      height: size,
    }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <radialGradient id="sunGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#fef3c7" />
            <stop offset="50%" stopColor={lightColor} />
            <stop offset="100%" stopColor={lightColor} stopOpacity={0.8} />
          </radialGradient>
          <filter id="sunGlow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="rayGlow">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={size * 0.38}
          fill="#111827"
          stroke="#374151"
          strokeWidth={2}
        />

        {/* Intensity percentage arc */}
        <circle
          cx={centerX}
          cy={centerY}
          r={size * 0.34}
          fill="none"
          stroke={lightColor}
          strokeWidth={4}
          strokeDasharray={`${2 * Math.PI * size * 0.34 * normalizedValue} ${2 * Math.PI * size * 0.34}`}
          strokeLinecap="round"
          transform={`rotate(-90 ${centerX} ${centerY})`}
          opacity={0.7}
        />

        {/* Sun rays */}
        {rays.map((ray, index) => (
          <line
            key={index}
            x1={ray.x1}
            y1={ray.y1}
            x2={ray.x2}
            y2={ray.y2}
            stroke={lightColor}
            strokeWidth={3}
            strokeLinecap="round"
            filter="url(#rayGlow)"
            opacity={0.5 + intensity * 0.5}
          >
            <animate
              attributeName="opacity"
              values={`${0.5 + intensity * 0.5};${0.3 + intensity * 0.4};${0.5 + intensity * 0.5}`}
              dur={`${1.5 + index * 0.1}s`}
              repeatCount="indefinite"
            />
          </line>
        ))}

        {/* Outer glow */}
        <circle
          cx={centerX}
          cy={centerY}
          r={sunRadius + 3}
          fill={lightColor}
          opacity={0.15 * intensity}
          filter="url(#sunGlow)"
        />

        {/* Sun center */}
        <circle
          cx={centerX}
          cy={centerY}
          r={sunRadius}
          fill="url(#sunGradient)"
          filter="url(#sunGlow)"
        />

        {/* Inner highlight */}
        <circle
          cx={centerX - sunRadius * 0.2}
          cy={centerY - sunRadius * 0.2}
          r={sunRadius * 0.25}
          fill="white"
          opacity={0.3}
        />

        {/* Percentage text below the sun graphic */}
        <text
          x={centerX}
          y={size - 8}
          textAnchor="middle"
          fill="#9ca3af"
          fontSize={Math.max(8, 10 * scale)}
          fontFamily="Inter, sans-serif"
        >
          {Math.round(normalizedValue * 100)}%
        </text>
      </svg>
    </Box>
  );
}

LightMeter.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  size: PropTypes.number,
  optimalMin: PropTypes.number,
  optimalMax: PropTypes.number,
  color: PropTypes.string,
};

export default LightMeter;
